import { useState, useEffect, useRef } from 'react'

/**
 * Custom hook for intersection observer preloading
 * Preloads a dynamic import when the element comes into view
 */
export const useIntersectionPreload = (importFn: () => Promise<any>) => {
	const [isPreloaded, setIsPreloaded] = useState(false)
	const ref = useRef<HTMLDivElement>(null)

	useEffect(() => {
		if (isPreloaded) return

		const observer = new IntersectionObserver(
			([entry]) => {
				// isPreloaded is always false here — the effect re-runs and
				// early-returns once it flips
				if (entry.isIntersecting) {
					// Preload when section comes into view
					importFn().then(() => {
						setIsPreloaded(true)
					}).catch((error) => {
						console.warn('Failed to preload component:', error)
					})
				}
			},
			{
				threshold: 0.1, // Trigger when 10% visible
				rootMargin: '50px' // Start preloading 50px before entering viewport
			}
		)

		if (ref.current) {
			observer.observe(ref.current)
		}

		return () => {
			observer.disconnect()
		}
	}, [importFn, isPreloaded])

	return { ref, isPreloaded }
}